import React from 'react';
import { useTranslation } from 'react-i18next';

const EventFilters = ({ filters, onFilterChange, onRefresh, loading }) => {
  const { t } = useTranslation();

  const eventTypes = [
    { value: '', label: t("common.all") },
    { value: 'Event', label: t("player.event") },
    { value: 'Match', label: t("player.match") },
    { value: 'Training', label: t("player.training") }
  ];

  const eventStatuses = [
    { value: '', label: t("common.all") },
    { value: 'Upcoming', label: t("player.upcoming") },
    { value: 'In Progress', label: t("player.inProgress") },
    { value: 'Completed', label: t("player.completed") }
  ];

  const sortOptions = [
    { value: 'startDate', label: t("player.startDate") },
    { value: 'createdAt', label: t("player.createdAt") },
    { value: 'name', label: t("player.eventName") }
  ];

  const sortOrders = [
    { value: 'asc', label: t("common.ascending") },
    { value: 'desc', label: t("common.descending") }
  ];

  const limitOptions = [
    { value: 5, label: '5' },
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' }
  ];

  const handleFilterChange = (field, value) => {
    onFilterChange({ [field]: value });
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          {t("player.filterAndSort")}
        </h3>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white rounded-lg transition-colors duration-200"
        >
          <svg 
            className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {loading ? t("common.refreshing") : t("common.refresh")}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        {/* Event Type Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("player.eventType")}
          </label>
          <select
            value={filters.eventType}
            onChange={(e) => handleFilterChange('eventType', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
          >
            {eventTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Event Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("player.eventStatus")}
          </label>
          <select
            value={filters.eventStatus}
            onChange={(e) => handleFilterChange('eventStatus', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
          >
            {eventStatuses.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        {/* Sort By */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("common.sortBy")}
          </label>
          <select
            value={filters.sortBy}
            onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Sort Order */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("common.sortOrder")}
          </label>
          <select
            value={filters.sortOrder}
            onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
          >
            {sortOrders.map((order) => (
              <option key={order.value} value={order.value}>
                {order.label}
              </option>
            ))}
          </select>
        </div>

        {/* Items Per Page */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("common.itemsPerPage")}
          </label>
          <select
            value={filters.limit}
            onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
          >
            {limitOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Clear Filters */}
        <div className="flex items-end">
          <button
            onClick={() => onFilterChange({
              eventType: '',
              eventStatus: '',
              sortBy: 'startDate',
              sortOrder: 'asc',
              limit: 10
            })}
            className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200 border border-gray-300"
          >
            {t("common.clearFilters")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventFilters; 